import {
  useDataTable,
} from '@uhg-abyss/web/hooks/useDataTable';
import { Row } from '@tanstack/react-table';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { ClosedLoopStatus, TMemberHistoryTableRow } from '@repo/utils';

type CellActions = any;

const SdohTableRowsContext = createContext<string[]>([]);

const isOlderThan45Days = (date: string) => {
  return dayjs().diff(dayjs(date), 'days') > 45;
};

// Proper React component to read context and apply bold styling per cell
const SdohCell: React.FC<{ value: unknown; row: TMemberHistoryTableRow }> = ({ value, row }) => {
  const matchValues = useContext(SdohTableRowsContext);
  const matchPrefix = row.match.split('.')[0];
  const isSingleScreener = !matchValues.includes(matchPrefix);
  const shouldBold =
    (row.closedLoopStatus === ClosedLoopStatus.PENDING || isSingleScreener) &&
    isOlderThan45Days(row.date);
  return <span style={{ fontWeight: shouldBold ? 'bold' : 'normal' }}>{String(value ?? '')}</span>;
};

export type MemberHistoryTableProps = {
  rows: TMemberHistoryTableRow[];
  onClickEdit: ({ row }: CellActions) => void;
  checkEditDisabled: (row: Row<TMemberHistoryTableRow>) => boolean;
};

export function MemberHistoryTable({
  rows,
  onClickEdit,
  checkEditDisabled,
}: MemberHistoryTableProps) {
  const [matchList, setMatchList] = useState<string[]>([]);

  const tableProps = useDataTable<TMemberHistoryTableRow>({
    initialData: rows,
    initialColumns: MemberHistoryTableColumns,
    rowIdKey: 'match',
    actionColumnConfig: {
      actionMode: 'button',
      items: [
        {
          label: 'Edit',
          variant: 'text',
          color: 'brand',
          onClick: ({ row }) => onClickEdit({ row }),
          checkDisabled: checkEditDisabled,
        },
      ],
    },
  });

  useEffect(() => {
    //create a list of match values that have referrals for their corresponding screeners
    const matchCount = new Map();
    rows.forEach((row) => {
      const matchPrefix = row.match.split('.')[0];
      matchCount.set(matchPrefix, (matchCount.get(matchPrefix) || 0) + 1);
    });
    const duplicates = Array.from(matchCount.entries())
      .filter(([_, count]) => count > 1)
      .map(([matchPrefix]) => matchPrefix);
    setMatchList(duplicates);
    tableProps.setData(rows);
  }, [rows]);

  return (
    <SdohTableRowsContext.Provider value={matchList}>
      <div className="sdoh-member-history-table">
        <style>{`
          .sdoh-member-history-table .abyss-data-table-header-data { color: #000000; }
          .sdoh-member-history-table .abyss-button-root { font-size: 16px; }
          .sdoh-member-history-table .abyss-data-table-header-container { justify-content: center; }
          .sdoh-member-history-table th:last-child,
          .sdoh-member-history-table td:last-child { border-left-width: 3px !important; }
        `}</style>
        <DataTable
          tableState={tableProps}
          title="Member history"
          hideHeader={true}
        >
          <DataTable.Table />
        </DataTable>
      </div>
    </SdohTableRowsContext.Provider>
  );
}

export const MemberHistoryTableColumns = [
  {
    id: 'date',
    header: 'Date',
    accessorKey: 'date',
    size: 100,
    cell: ({ getValue, row }) => (
      <SdohCell value={dayjs(getValue()).format('MM/DD/YYYY')} row={row.original} />
    ),
  },
  {
    id: 'type',
    header: 'Type',
    accessorKey: 'type',
    size: 100,
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'need',
    header: 'Need',
    accessorKey: 'need',
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'programType',
    header: 'Program type',
    accessorKey: 'programType',
    size: 120,
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'programSubType',
    header: 'Program subtype',
    accessorKey: 'programSubType',
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'programNameAndOrganizationName',
    header: 'Program and Organization name',
    accessorKey: 'programNameAndOrganizationName',
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'servicedThrough',
    header: 'Serviced through',
    accessorKey: 'servicedThrough',
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'closedLoopStatus',
    header: 'Closed loop status',
    accessorKey: 'closedLoopStatus',
    size: 100,
    cell: ({ getValue, row }) => <SdohCell value={getValue()} row={row.original} />,
  },
  {
    id: 'reasonNotFulfilled',
    header: 'Reason not fulfilled',
    accessorKey: 'reasonNotFulfilled',
    cell: ({ getValue, row }) => {
      const raw = getValue() as string;
      const formatted = raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : '';
      return <SdohCell value={formatted} row={row.original} />;
    },
  },
];
